import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ArrowRight, Loader2, MapPin, Building2, Users, Layers, GraduationCap, IdCard,
  CheckCircle2, Clock, TrendingUp, Timer, AlertTriangle, Wrench, Calendar
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getSchoolDetails } from '../api';

function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-white/[0.04] last:border-b-0">
      {Icon && (
        <div className="icon-chip shrink-0" style={{ background: 'rgba(59,130,246,0.12)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.22)', width: 30, height: 30 }}>
          <Icon size={14} />
        </div>
      )}
      <div className="text-slate-400 text-[11.5px] font-semibold w-32 shrink-0">{label}</div>
      <div className="text-slate-100 text-[12.5px] font-medium flex-1 min-w-0 truncate" title={value}>{value || '—'}</div>
    </div>
  );
}

function Kpi({ label, value, tint = '#60A5FA', icon: Icon, unit }) {
  return (
    <div className="panel px-3 py-3">
      <div className="flex items-start justify-between">
        <div className="text-slate-400 text-[11.5px] font-semibold">{label}</div>
        {Icon && (
          <div className="icon-chip" style={{ background: `${tint}18`, color: tint, border: `1px solid ${tint}30`, width: 30, height: 30 }}>
            <Icon size={14} />
          </div>
        )}
      </div>
      <div className="mt-1 text-white text-[22px] font-extrabold tabular-nums">{value}</div>
      {unit && <div className="text-slate-500 text-[10.5px]">{unit}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  open:      { pill: 'pill-amber', label: 'مفتوح' },
  completed: { pill: 'pill-green', label: 'مكتمل' },
};

function StatusPill({ status, isCompleted }) {
  const p = isCompleted ? STATUS_STYLES.completed : STATUS_STYLES.open;
  return <span className={`pill ${p.pill}`}>{status || p.label}</span>;
}

function PriorityPill({ value }) {
  if (!value) return <span className="text-slate-500 text-[11px]">—</span>;
  const isCritical = /حرج|critical|urgent/i.test(value);
  const isHigh = /مرتفع|high/i.test(value);
  const cls = isCritical ? 'pill-red' : isHigh ? 'pill-amber' : 'pill-blue';
  return <span className={`pill ${cls}`}>{value}</span>;
}

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB'); } catch (e) { return String(d); }
}

export default function SchoolDetails() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    getSchoolDetails(id)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const school = data?.school;
  const kpis = data?.kpis || {};
  const tickets = data?.tickets || [];
  const cats = data?.categoriesBreakdown || [];
  const hasCoords = school && school.latitude != null && school.longitude != null;
  const fmt = (n) => (n ?? 0).toLocaleString('en-US');

  return (
    <div dir="rtl" className="min-h-screen bg-[#0A101C] flex">
      <Sidebar />
      <main className="flex-1 p-4 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-slate-400 text-[11px] font-medium">لوحة القيادة › تفاصيل المدرسة</div>
            <h1 className="text-white text-[22px] font-extrabold tracking-tight leading-tight mt-0.5 truncate">
              {school?.name || `مدرسة ${id}`}
            </h1>
            {school && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {school.region && <span className="pill pill-blue">{school.region}</span>}
                {school.governorate && <span className="pill">{school.governorate}</span>}
                {school.gender && <span className="pill">{school.gender}</span>}
                {school.school_type && <span className="pill">{school.school_type}</span>}
                <span className="pill">الرقم الوزاري: <b className="text-white mr-1">{school.ministerial_id}</b></span>
              </div>
            )}
          </div>
          <button onClick={() => nav('/')} className="panel panel-hoverable px-3 py-2 flex items-center gap-2 text-slate-200 text-[12.5px] font-semibold">
            <ArrowRight size={14} /> العودة إلى لوحة التحكم
          </button>
        </div>

        {loading && (
          <div className="panel p-10 flex items-center justify-center gap-2 text-slate-300">
            <Loader2 size={18} className="animate-spin" /> جاري تحميل بيانات المدرسة...
          </div>
        )}
        {error && !loading && (
          <div className="panel p-10 flex flex-col items-center justify-center gap-3 text-center" data-testid="school-not-found">
            <AlertTriangle size={38} className="text-amber-400" />
            <div className="text-white text-lg font-bold">تعذر تحميل بيانات المدرسة</div>
            <div className="text-slate-400 text-sm max-w-md">{error === 'School not found' ? 'المدرسة المطلوبة غير موجودة في البيانات المستوردة. قد يكون الرقم الوزاري غير مربوط بأي مدرسة.' : `التفاصيل: ${error}`}</div>
            <button onClick={() => nav('/schools')} className="mt-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold">تصفح قائمة المدارس</button>
          </div>
        )}

        {data && !loading && (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-5 gap-3 mb-4">
              <Kpi label="إجمالي البلاغات" value={fmt(kpis.totalTickets)} tint="#60A5FA" icon={TrendingUp} unit="بلاغ" />
              <Kpi label="المكتملة" value={fmt(kpis.completedTickets)} tint="#22C55E" icon={CheckCircle2} unit="بلاغ" />
              <Kpi label="المفتوحة" value={fmt(kpis.openTickets)} tint="#EF4444" icon={Clock} unit="بلاغ" />
              <Kpi label="نسبة الإنجاز" value={`${kpis.completionRate ?? 0}%`} tint="#22C55E" icon={CheckCircle2} />
              <Kpi label="متوسط مدة الإغلاق" value={kpis.avgCloseDays ?? 0} tint="#F59E0B" icon={Timer} unit="يوم" />
            </div>

            <div className="grid grid-cols-12 gap-3 mb-4">
              {/* Info */}
              <div className="col-span-4">
                <div className="panel p-4">
                  <div className="panel-header">
                    <div className="flex items-center gap-2">
                      <div className="icon-chip" style={{ background: 'rgba(59,130,246,0.14)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.25)', width: 28, height: 28 }}>
                        <IdCard size={14} />
                      </div>
                      <div>
                        <div className="panel-title">معلومات المدرسة</div>
                        <div className="panel-subtitle mt-0.5">البيانات المستوردة من ملف Excel</div>
                      </div>
                    </div>
                  </div>
                  <InfoRow label="الرقم الوزاري" value={school?.ministerial_id} icon={IdCard} />
                  <InfoRow label="اسم المدرسة" value={school?.name} icon={GraduationCap} />
                  <InfoRow label="المنطقة" value={school?.region} icon={Layers} />
                  <InfoRow label="المحافظة" value={school?.governorate} icon={MapPin} />
                  <InfoRow label="إدارة التعليم" value={school?.education_office} icon={Building2} />
                  <InfoRow label="المدينة" value={school?.city} icon={MapPin} />
                  <InfoRow label="نوع المدرسة" value={school?.school_type} icon={GraduationCap} />
                  <InfoRow label="الجنس" value={school?.gender} icon={Users} />
                </div>

                {/* Categories breakdown */}
                {cats.length > 0 && (
                  <div className="panel p-4 mt-3">
                    <div className="panel-header">
                      <div className="panel-title">توزيع البلاغات حسب الفئة</div>
                    </div>
                    <ul className="space-y-1.5">
                      {cats.map((c) => (
                        <li key={c.name} className="flex items-center justify-between text-[12px]">
                          <span className="text-slate-200 truncate flex-1" title={c.name}>{c.name}</span>
                          <span className="text-white font-bold tabular-nums">{c.value.toLocaleString('en-US')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Map */}
              <div className="col-span-8">
                <div className="panel p-4">
                  <div className="panel-header">
                    <div className="flex items-center gap-2">
                      <div className="icon-chip" style={{ background: 'rgba(34,197,94,0.14)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.25)', width: 28, height: 28 }}>
                        <MapPin size={14} />
                      </div>
                      <div>
                        <div className="panel-title">موقع المدرسة على الخريطة</div>
                        <div className="panel-subtitle mt-0.5">
                          {hasCoords ? `${school.latitude?.toFixed(5)}, ${school.longitude?.toFixed(5)}` : 'لا توجد إحداثيات'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ height: 340 }} className="rounded-xl overflow-hidden">
                    {hasCoords ? (
                      <MapContainer center={[school.latitude, school.longitude]} zoom={13} scrollWheelZoom={false} style={{ height: '100%', width: '100%', background: '#0A1120' }}>
                        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                        <CircleMarker center={[school.latitude, school.longitude]} radius={12}
                          pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.85, weight: 2 }}>
                          <Popup>
                            <div dir="rtl" style={{ fontSize: 12 }}>
                              <div style={{ fontWeight: 700 }}>{school.name}</div>
                              <div>{school.governorate}</div>
                            </div>
                          </Popup>
                        </CircleMarker>
                      </MapContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-500 text-sm">لا توجد إحداثيات جغرافية لهذه المدرسة</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tickets timeline */}
            <div className="panel p-4">
              <div className="panel-header">
                <div className="flex items-center gap-2">
                  <div className="icon-chip" style={{ background: 'rgba(245,158,11,0.14)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.25)', width: 28, height: 28 }}>
                    <Wrench size={14} />
                  </div>
                  <div>
                    <div className="panel-title">جدول البلاغات والصيانة</div>
                    <div className="panel-subtitle mt-0.5">مرتب زمنياً — من الأحدث إلى الأقدم</div>
                  </div>
                </div>
                <span className="pill pill-blue">{tickets.length} بلاغ</span>
              </div>
              {tickets.length === 0 ? (
                <div className="text-slate-500 text-sm text-center py-10">لا توجد بلاغات مسجلة لهذه المدرسة</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px] text-right">
                    <thead>
                      <tr className="text-slate-400 border-b border-white/5">
                        <th className="pb-2 pr-2 font-semibold text-right">رقم البلاغ</th>
                        <th className="pb-2 font-semibold text-right">الفئة</th>
                        <th className="pb-2 font-semibold text-right">الحالة</th>
                        <th className="pb-2 font-semibold text-right">الأولوية</th>
                        <th className="pb-2 font-semibold text-right">المقاول</th>
                        <th className="pb-2 font-semibold">تاريخ الإنشاء</th>
                        <th className="pb-2 font-semibold">تاريخ الإغلاق</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t, i) => (
                        <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors">
                          <td className="py-2 pr-2 text-slate-100 font-mono text-[11.5px]">{t.ticket_id}</td>
                          <td className="py-2 text-slate-300 truncate max-w-[140px]">{t.category || '—'}</td>
                          <td className="py-2"><StatusPill status={t.status} isCompleted={t.isCompleted} /></td>
                          <td className="py-2"><PriorityPill value={t.priority} /></td>
                          <td className="py-2 text-slate-300 truncate max-w-[140px]" title={t.contractor}>{t.contractor || '—'}</td>
                          <td className="py-2 text-slate-400 text-center tabular-nums">{formatDate(t.created_date)}</td>
                          <td className="py-2 text-slate-400 text-center tabular-nums">{formatDate(t.closed_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Vertical activity timeline */}
            {tickets.length > 0 && (
              <div className="panel p-4 mt-3 mb-4">
                <div className="panel-header">
                  <div className="flex items-center gap-2">
                    <div className="icon-chip" style={{ background: 'rgba(168,85,247,0.14)', color: '#C084FC', border: '1px solid rgba(168,85,247,0.25)', width: 28, height: 28 }}>
                      <Calendar size={14} />
                    </div>
                    <div>
                      <div className="panel-title">الخط الزمني للأنشطة</div>
                      <div className="panel-subtitle mt-0.5">أحدث 12 نشاطاً</div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute top-0 bottom-0 right-3 w-0.5 bg-white/[0.06]" />
                  <div className="space-y-3">
                    {tickets.slice(0, 12).map((t, i) => (
                      <div key={i} className="relative pr-8">
                        <div className={`absolute right-2 top-1 w-3 h-3 rounded-full border-2 border-[#0A101C] ${t.isCompleted ? 'bg-green-500' : /حرج/i.test(t.priority || '') ? 'bg-red-500' : 'bg-amber-400'}`} />
                        <div className="panel px-3 py-2">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-slate-100 text-[12.5px] font-bold font-mono">{t.ticket_id}</span>
                              <StatusPill status={t.status} isCompleted={t.isCompleted} />
                              <PriorityPill value={t.priority} />
                            </div>
                            <span className="text-slate-500 text-[11px] tabular-nums">{formatDate(t.created_date)}</span>
                          </div>
                          <div className="text-slate-300 text-[12px] mt-1 truncate">
                            <span className="text-slate-400">الفئة:</span> {t.category || '—'}
                            <span className="text-slate-500 mx-2">•</span>
                            <span className="text-slate-400">المقاول:</span> {t.contractor || '—'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Supply · Contractor · Supervisor · Engineer · Visits — cross-module school view */}
            <SchoolCrossModules schoolId={id} contractor={data?.school?.contractor} />
          </>
        )}
      </main>
    </div>
  );
}

/**
 * SchoolCrossModules — shows the school's cross-module footprint:
 * live Supply rows filtered to this school, plus placeholder tiles for
 * Contractor / Supervisor / Facility Engineer / Visits until those Excel
 * files are imported.
 */
function SchoolCrossModules({ schoolId, contractor }) {
  const [supply, setSupply] = useState({ rows: [], total: 0, loading: true });

  useEffect(() => {
    if (!schoolId) return;
    let alive = true;
    import('../api').then(({ api }) =>
      api.post('/supply/rows', { search: String(schoolId), page_size: 8 })
        .then(({ data }) => { if (alive) setSupply({ rows: data.rows || [], total: data.total || 0, loading: false }); })
        .catch(() => { if (alive) setSupply({ rows: [], total: 0, loading: false }); })
    );
    return () => { alive = false; };
  }, [schoolId]);

  const statusLabel = { delivered: 'تم التوريد', pending: 'قيد التوريد', delayed: 'متأخر' };
  const statusPill  = { delivered: 'pill-green', pending: 'pill-amber', delayed: 'pill-red' };

  return (
    <div className="space-y-6 mt-6" data-testid="school-cross-modules">
      {/* Supply */}
      <div className="panel p-5">
        <div className="panel-header">
          <div>
            <div className="panel-title">التوريد</div>
            <div className="panel-subtitle">أصناف مستلمة / قيد التوريد لهذه المدرسة</div>
          </div>
          <span className="pill pill-purple">{supply.total} سجل</span>
        </div>
        {supply.loading ? (
          <div className="text-slate-400 text-sm py-4 text-center">جاري التحميل...</div>
        ) : supply.rows.length === 0 ? (
          <div className="text-slate-500 text-sm py-4 text-center">لا توجد سجلات توريد مسجلة لهذه المدرسة بعد.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-slate-400 border-b hair">
                  <th className="text-right py-2 font-semibold">الشركة</th>
                  <th className="text-right py-2 font-semibold">الصنف</th>
                  <th className="text-left  py-2 font-semibold">الكمية</th>
                  <th className="text-right py-2 font-semibold">الحالة</th>
                  <th className="text-right py-2 font-semibold">التاريخ</th>
                </tr>
              </thead>
              <tbody>
                {supply.rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/[0.03]">
                    <td className="py-2 text-slate-200 truncate max-w-[200px]" title={r.company}>{r.company || '—'}</td>
                    <td className="py-2 text-slate-300 truncate max-w-[200px]" title={r.item}>{r.item || '—'}</td>
                    <td className="py-2 text-white tabular-nums text-left">{(r.quantity ?? 0).toLocaleString('en-US')}</td>
                    <td className="py-2"><span className={`pill ${statusPill[r.status] || ''}`}>{statusLabel[r.status] || r.status || '—'}</span></td>
                    <td className="py-2 text-slate-400 tabular-nums">{r.delivery_date ? String(r.delivery_date).split('T')[0] : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Placeholders for modules whose Excel hasn't been imported yet */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlaceholderCard title="الجاهزية"       hint="يتوفر بعد استيراد ملف الجاهزية" tint="#10B981" />
        <PlaceholderCard title="المقاول"         hint={contractor ? `المقاول الحالي: ${contractor}` : 'يتوفر بعد ربط المدرسة بعقد'} tint="#8B5CF6" />
        <PlaceholderCard title="المشرف"          hint="يتوفر بعد استيراد ملف المشرفين" tint="#6FA8FF" />
        <PlaceholderCard title="مهندس المرافق"   hint="يتوفر بعد استيراد ملف مهندسي المرافق" tint="#F59E0B" />
        <PlaceholderCard title="الزيارات الميدانية" hint="يتوفر بعد استيراد ملف الزيارات" tint="#A78BFA" full />
      </div>
    </div>
  );
}

function PlaceholderCard({ title, hint, tint, full }) {
  return (
    <div className={`panel p-4 ${full ? 'md:col-span-2' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-slate-100 text-[13.5px] font-bold">{title}</div>
          <div className="text-slate-400 text-[12px] mt-1">{hint}</div>
        </div>
        <span className="pill" style={{ color: tint, borderColor: `${tint}40`, background: `${tint}12` }}>قيد التفعيل</span>
      </div>
    </div>
  );
}
